import PropTypes from "prop-types";
import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  useExpanded,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable
} from "react-table";

import { Card, CardBody, Col, Row, Table } from "reactstrap";
import { DefaultColumnFilter } from "./filters";

import { COLORS_SECTION_KEYWORD, COLORS_STATUS_POST, SECTION_KEYWORD_OPTIONS, STATUS_POST_OPTIONS } from "helpers/constans";
import { formatNumberWithCommas } from "helpers/format";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate";
import { useDraggable } from "react-use-draggable-scroll";
import TooltipCustom from "./TooltipCustom";

interface NewsTableContainerProps {
  className?: any;
  columns?: any;
  data?: any;
  customPageSize?: any;
  tableClass?: any;
  theadClass?: any;
  trClass?: any;
  thClass?: any;
  divClass?: any;
  scrollHorizontalAuto?: boolean;
  description?: string;
  customPageIndex?: any;
  customPageCount?: any;
  totalRecords?: any;
  handleChangePage?: any;
  sorting?: { sort_by: string, order_by: string };
  handleChangeSorting?: (sortBy: { sort_by: string | undefined | null, order_by: string | undefined | null }) => void,
  manualSorting?: boolean,
  isLoading?: boolean,
  isShowPagination?: boolean,
  isShowLoadingBottom?: boolean,
  isShowColumn?: boolean,
  classBackgroundWhenActiveSort?: string,
  isScrollToTop?: boolean,
  typeShowList?: 'CARD' | 'TABLE',
  handleClickDetailCard?: (item: any) => void,
}

const NewsTableContainer = ({
  columns = [],
  data = [],
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  scrollHorizontalAuto = false,
  description,
  customPageIndex,
  customPageCount,
  handleChangePage,
  totalRecords,
  sorting,
  handleChangeSorting,
  manualSorting = false,
  isLoading = false,
  isShowPagination = true,
  isShowLoadingBottom = false,
  isShowColumn = true,
  classBackgroundWhenActiveSort = '',
  isScrollToTop = true,
  typeShowList = 'TABLE',
  handleClickDetailCard,
}: NewsTableContainerProps) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageIndex: +customPageIndex || 0,
        pageSize: +customPageSize || 0,
        selectedRowIds: 0,
      },
      pageCount: +customPageCount || 0,
      manualPagination: true,
      // manualSorting: true,
      manualSortBy: true,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );
  const { t } = useTranslation();

  const [isScrollBottom, setIsScrollBottom] = useState<boolean>(false);
  const [scrollLeft, setScrollLeft] = useState<any>(0);

  const STATUS_POST_OPTIONS_LANG = STATUS_POST_OPTIONS?.map((item: any) => ({
    value: item?.value,
    label: t(item?.label),
  }));

  const SECTION_KEYWORD_OPTIONS_LANG = SECTION_KEYWORD_OPTIONS?.map((item: any) => ({
    value: item?.value,
    label: t(item?.label),
  }));

  const thRefs = useRef<any>([]);
  const tableRef = useRef<any>(null);
  const warperTableRef = useRef<any>(null);
  const elementFixedRef = useRef<any>(null);
  const elementHeaderFixedRef = useRef<any>(null);
  const elementTable: any = tableRef?.current?.getBoundingClientRect() || {};
  const elementWarperTable: any = warperTableRef?.current?.getBoundingClientRect() || {};

  const [isShowHeaderTableFixed, setIsShowHeaderTableFixed] = useState<boolean>(false);

  const { events } = useDraggable(warperTableRef);

  const generateSortingIndicator = (column: any) => {
    return (
      column?.sortable ? (
        <span style={{ display: 'inline-flex', justifyContent: 'center', width: '12px', marginLeft: '2px', verticalAlign: 'middle' }}>
          {(column.id === sorting?.sort_by && sorting?.order_by === 'ASC') && <i className={`ri-arrow-up-line fs-13 text-primary`} />}
          {(column.id === sorting?.sort_by && sorting?.order_by === 'DESC') && <i className={`ri-arrow-down-line fs-13 text-primary`} />}
        </span>
      ) : (
        ""
      )
    );
  };
  const generateDescription = (column: any) => {
    return (
      column?.description ? (
        <TooltipCustom
          title={column?.description || ''}
          className="d-inline-block vertical-align-middle ms-1"
          style={{ transform: 'translateY(2px)' }}
        >
          <i className="ri-question-line align-bottom text-secondary" ></i>
        </TooltipCustom>
      ) : (<></>)
    );
  };

  const handleScroll = (e: any) => {
    setScrollLeft((prev: any) => e?.target?.scrollLeft)
    if (warperTableRef?.current && elementFixedRef?.current) {
      // If warperTableRef scrolls, then scroll elementFixedRef as well
      if (e.target === warperTableRef?.current) {
        elementFixedRef.current.scrollLeft = e?.target?.scrollLeft;
        elementHeaderFixedRef.current.scrollLeft = e?.target?.scrollLeft - (window.innerWidth < 500 ? 3 : 0);
      }
      // If elementFixedRef scrolls, then scroll warperTableRef as well
      else if (e.target === elementFixedRef.current) {
        warperTableRef.current.scrollLeft = e?.target?.scrollLeft;
      }
    }

  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window?.pageYOffset || document?.documentElement?.scrollTop || 0;
      const windowHeight = window?.innerHeight || document.documentElement?.clientHeight;
      const documentHeight = document?.documentElement?.scrollHeight;
      const MARGIN_BOTTOM_HEIGHT = 170; // 170px
      if ((scrollTop + windowHeight + MARGIN_BOTTOM_HEIGHT) >= documentHeight) {
        setIsScrollBottom((prev) => true);
      } else {
        setIsScrollBottom((prev) => false);
      }

      const rect = warperTableRef?.current?.getBoundingClientRect();
      const distanceFromTop = rect?.top;
      if (distanceFromTop <= 70) {
        elementHeaderFixedRef.current.scrollLeft = elementFixedRef?.current?.scrollLeft - (window.innerWidth < 500 ? 3 : 0);
        setIsShowHeaderTableFixed((prev) => true);
      } else {
        setIsShowHeaderTableFixed((prev) => false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const onClickCard = (detail: any) => {
    handleClickDetailCard && handleClickDetailCard(detail);
  };

  return (
    <Fragment>
      <div className={`card-custom-pagination ${typeShowList !== 'CARD' ? 'd-none' : ''}`}>
        <Row>
          <Col sm={12} style={{ height: '5px', marginBottom: '2px' }}>
            <div className={`infinite-loading-outer ${isLoading && 'is-show-loading'}`}>
              <div className="infinite-loading-inner"></div>
            </div>
          </Col>
        </Row>
        <Row>
          {(typeShowList === 'CARD') && (page || []).map((row: any) => (
            <Col md={6} lg={6} key={row?.original?.id}>
              <Card className="border" >
                <CardBody style={{ position: 'relative' }}>
                  <div className="d-sm-flex" >
                    <div className="flex-grow-1">
                      <div className="d-flex mb-3 justify-content-between align-items-center">
                        <div>
                          <span className={`rounded-pill badge bg-success me-2`}>
                            {row?.original?.category_id || ''}
                          </span>
                          <span key={row?.original?.section} className={`rounded-pill me-2 badge  bg-${COLORS_SECTION_KEYWORD[`${row?.original?.section || ''}`] || 'success'}-subtle text-${COLORS_SECTION_KEYWORD[`${row?.original?.section || ''}`] || 'success'}`}>
                            {SECTION_KEYWORD_OPTIONS_LANG?.find((s) => String(s?.value) === String(row?.original?.section))?.label || ''}
                          </span>
                        </div>
                        <div>
                          <span className={`rounded-pill badge bg-${COLORS_STATUS_POST[`${row?.original?.status || 0}`] || 'success'}`}>
                            {STATUS_POST_OPTIONS_LANG?.find((s) => String(s?.value) === String(row?.original?.status))?.label || ''}
                          </span>
                        </div>
                      </div>
                      <h5 className="cursor-pointer hover-underline" onClick={() => onClickCard({ ...row?.original, index: row?.index })}>{row?.original?.title}</h5>
                      <ul className="list-inline mb-0 d-flex justify-content-end">
                        <li className="list-inline-item">
                          <i className="ri-calendar-2-fill text-success align-middle me-1"></i>
                          {String(row?.original?.updated_at || '').split(' ')[0] || ''}
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
      <div className={typeShowList !== 'TABLE' ? 'd-none' : ''}>
        <div className={`table-custom-pagination ${divClass}`} {...events} ref={warperTableRef} onScroll={handleScroll}>
          <Table hover {...getTableProps()} className={`${tableClass} ${Number(scrollLeft || 0) > 0 && 'is-scroll'}`} style={{ minHeight: '70px' }} >
            <thead className={theadClass} ref={tableRef}>
              {headerGroups.map((headerGroup: any) => (
                <tr className={trClass} key={headerGroup.id}  {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: any, i: number) => (
                    <th
                      key={column.id}
                      ref={el => thRefs.current[i] = el}
                      className={`${thClass || ''} ${column?.thClass || ''} ${(column?.sortable && column.id === sorting?.sort_by) && classBackgroundWhenActiveSort}`}
                      {...(column?.thWidth ? { width: column?.thWidth } : {})}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      onClick={(e) => {

                        if (!column?.sortable) { return };

                        if ((!sorting?.sort_by || !sorting?.order_by)) {
                          const sortBy = { sort_by: column?.id, order_by: 'DESC' };
                          handleChangeSorting && handleChangeSorting(sortBy);
                        } else if (sorting?.sort_by && sorting?.order_by) {
                          const sortBy = { sort_by: column?.id, order_by: sorting?.order_by === 'DESC' && sorting?.sort_by === column?.id ? 'ASC' : 'DESC' };
                          handleChangeSorting && handleChangeSorting(sortBy);
                        }
                      }}
                    >

                      {column?.thComponent ? <>{column?.thComponent()}</> : column.render("Header")}
                      {generateDescription(column)}
                      {generateSortingIndicator(column)}

                    </th>
                  ))}
                </tr>
              ))}
              {(headerGroups?.length) && (<tr >
                <th colSpan={headerGroups[0]?.headers?.length} className="py-0 px-0" style={{ borderBottomColor: 'transparent' }}>
                  <div className={`infinite-loading-outer ${isLoading && 'is-show-loading'}`}>
                    <div className="infinite-loading-inner"></div>
                  </div>
                </th>
              </tr>)}
            </thead>
            <tbody {...getTableBodyProps()}>
              {(typeShowList === 'TABLE') && page?.map((row: any) => {
                prepareRow(row);
                return (
                  <>
                    <tr key={row.getRowProps().key}>
                      {row.cells.map((cell: any) => {
                        return (
                          <td key={cell.id} {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  </>
                );
              })}
            </tbody>
          </Table>
        </div>
        <div
          className={`table-custom-pagination table-card overflow-x-auto m-0 hidden-scrollbar-os ${isShowHeaderTableFixed ? '' : 'd-none'}`}
          style={{
            position: 'fixed',
            top: 70,
            zIndex: 9,
            left: elementWarperTable?.left,
            width: elementWarperTable?.width,
            right: 0,

          }}
          ref={elementHeaderFixedRef}
        >
          <Table hover {...getTableProps()} className={`${tableClass} ${Number(scrollLeft || 0) > 0 && 'is-scroll'} mb-0`} style={{ height: 'auto', tableLayout: 'fixed' }} >
            <thead className={theadClass} style={{ width: elementTable?.width }}>
              {headerGroups.map((headerGroup: any) => (
                <tr className={trClass} key={headerGroup.id}  {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: any, i: number) => (
                    <th
                      key={column.id}
                      className={`${thClass || ''} ${column?.thClass || ''} ${(column?.sortable && column.id === sorting?.sort_by) && classBackgroundWhenActiveSort}`}
                      {...(column?.thWidth ? { width: thRefs.current[i]?.offsetWidth } : { width: thRefs.current[i]?.offsetWidth })}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      onClick={(e) => {

                        if (!column?.sortable) { return };

                        if ((!sorting?.sort_by || !sorting?.order_by)) {
                          const sortBy = { sort_by: column?.id, order_by: 'DESC' };
                          handleChangeSorting && handleChangeSorting(sortBy);
                        } else if (sorting?.sort_by && sorting?.order_by) {
                          const sortBy = { sort_by: column?.id, order_by: sorting?.order_by === 'DESC' && sorting?.sort_by === column?.id ? 'ASC' : 'DESC' };
                          handleChangeSorting && handleChangeSorting(sortBy);
                        }
                      }}
                    >

                      {column?.thComponent ? <>{column?.thComponent()}</> : column.render("Header")}
                      {generateDescription(column)}
                      {generateSortingIndicator(column)}

                    </th>
                  ))}
                </tr>
              ))}
              {(headerGroups?.length) && (<tr >
                <th colSpan={headerGroups[0]?.headers?.length} className="py-0 px-0" style={{ borderBottomColor: 'transparent' }}>
                  <div className={`infinite-loading-outer ${isLoading && 'is-show-loading'}`}>
                    <div className="infinite-loading-inner"></div>
                  </div>
                </th>
              </tr>)}
            </thead>
          </Table>
        </div>
        <div
          className="overflow-x-auto color-scrollbar-os"
          style={{
            position: 'fixed',
            zIndex: 999,
            left: elementWarperTable?.left,
            bottom: 0,
            width: elementWarperTable?.width,
            right: 0,
            opacity: isScrollBottom ? 0 : 1,
          }}
          ref={elementFixedRef}
          onScroll={handleScroll}
        >
          <div style={{ width: elementTable?.width, height: '1px' }}></div>
        </div>
      </div>
      {(isShowLoadingBottom && isLoading) && (<div className=" d-flex text-center align-items-center justify-content-center pt-3">
        <svg xmlns="http://www.w3.org/2000/svg" width="100px" height="60px" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">
          <g transform="translate(20 50)">
            <circle cx="0" cy="0" r="10" fill="#3498db">
              <animateTransform attributeName="transform" type="scale" begin="-0.375s" calcMode="spline" keySplines="0.3 0 0.7 1;0.3 0 0.7 1" values="0;1;0" keyTimes="0;0.5;1" dur="1s" repeatCount="indefinite"></animateTransform>
            </circle>
          </g><g transform="translate(40 50)">
            <circle cx="0" cy="0" r="10" fill="#53a6de">
              <animateTransform attributeName="transform" type="scale" begin="-0.25s" calcMode="spline" keySplines="0.3 0 0.7 1;0.3 0 0.7 1" values="0;1;0" keyTimes="0;0.5;1" dur="1s" repeatCount="indefinite"></animateTransform>
            </circle>
          </g><g transform="translate(60 50)">
            <circle cx="0" cy="0" r="10" fill="#5fb5ef">
              <animateTransform attributeName="transform" type="scale" begin="-0.125s" calcMode="spline" keySplines="0.3 0 0.7 1;0.3 0 0.7 1" values="0;1;0" keyTimes="0;0.5;1" dur="1s" repeatCount="indefinite"></animateTransform>
            </circle>
          </g><g transform="translate(80 50)">
            <circle cx="0" cy="0" r="10" fill="#5ca6d8">
              <animateTransform attributeName="transform" type="scale" begin="0s" calcMode="spline" keySplines="0.3 0 0.7 1;0.3 0 0.7 1" values="0;1;0" keyTimes="0;0.5;1" dur="1s" repeatCount="indefinite"></animateTransform>
            </circle>
          </g>
        </svg>
      </div>)}
      {isShowPagination && (
        <Row className="align-items-center mt-2 g-3 text-center text-sm-start overflow-x-auto">
          <div className="col-sm">
            <div className="text-muted">
              {t('Total')}:
              {" "}
              <span className="fw-semibold ms-1">{formatNumberWithCommas(totalRecords)}</span>
              {" "}
              {t('Results')}
            </div>
          </div>
          <div className="col-sm-auto">
            <ReactPaginate
              nextLabel={`${t('next')} >`}
              onPageChange={(page) => {
                if (handleChangePage) {
                  isScrollToTop && handleScrollToTop();
                  handleChangePage(page.selected);
                }
              }}
              forcePage={pageIndex}
              pageRangeDisplayed={3}
              marginPagesDisplayed={2}
              pageCount={customPageCount}
              previousLabel={`< ${t('previous')}`}
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              breakLabel="..."
              breakClassName="page-item"
              breakLinkClassName="page-link"
              containerClassName="pagination pagination-separated"
              activeClassName="active"
              renderOnZeroPageCount={null}
            />
          </div>
        </Row>)}
    </Fragment>
  );
};

NewsTableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default React.memo(NewsTableContainer);